import { Meta } from '@angular/platform-browser';

/**
 * Gemeinsame SEO-Helfer für die Shop-Produktseiten (marktbox 39/24, Extension
 * 40/25). Setzt Canonical, Open-Graph-Tags und JSON-LD (Product, FAQPage,
 * BreadcrumbList) – serverseitig (im Konstruktor/ngOnInit aufgerufen), damit
 * Crawler die Auszeichnung im SSR-HTML sehen. Muster analog zu den
 * rechner-Seiten.
 */

const SITE = 'https://marktbox.de';

export interface PdpSeoInput {
  document: Document;
  meta: Meta;
  /** LOCALE_ID, z. B. 'de' oder 'da-DK'. */
  locale: string;
  /** Routen-Slug unter /shop, z. B. 'marktbox-24'. */
  slug: string;
  ogTitle: string;
  ogDescription: string;
  product: {
    name: string;
    description: string;
    /** Bildpfad (assets/…) oder absolute URL. */
    image: string;
    /** Netto-Kaufpreis in EUR. */
    priceNet: number;
    /** Netto-Mietpreis pro Monat (optional) – erzeugt ein zweites Offer, Google zeigt „ab …“. */
    rentNet?: number;
    sku?: string;
  };
  faqs: { q: string; a: string }[];
}

function abs(path: string): string {
  return /^https?:\/\//.test(path) ? path : `${SITE}/${path.replace(/^\//, '')}`;
}

export function applyPdpSeo(input: PdpSeoInput): void {
  const { document, meta } = input;
  const prefix = input.locale.startsWith('da') ? 'da' : input.locale.startsWith('nl') ? 'nl' : 'de';
  const canonical = `${SITE}/${prefix}/shop/${input.slug}`;
  const image = abs(input.product.image);

  // Klassische Meta-Description (SISTRIX/Google): nutzt dieselbe Kurzbeschreibung
  // wie Open Graph, damit die PDPs nicht ohne Snippet crawlen.
  meta.updateTag({ name: 'description', content: input.ogDescription });

  meta.updateTag({ property: 'og:type', content: 'product' });
  meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
  meta.updateTag({ property: 'og:locale', content: prefix === 'da' ? 'da_DK' : prefix === 'nl' ? 'nl_NL' : 'de_DE' });
  meta.updateTag({ property: 'og:title', content: input.ogTitle });
  meta.updateTag({ property: 'og:description', content: input.ogDescription });
  meta.updateTag({ property: 'og:url', content: canonical });
  meta.updateTag({ property: 'og:image', content: image });

  meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
  meta.updateTag({ name: 'twitter:title', content: input.ogTitle });
  meta.updateTag({ name: 'twitter:description', content: input.ogDescription });
  meta.updateTag({ name: 'twitter:image', content: image });

  setCanonical(document, canonical);

  injectJsonLd(document, 'pdp-product-schema', {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: input.product.name,
    description: input.product.description,
    image,
    brand: { '@type': 'Brand', name: 'marktbox' },
    category: 'Verkaufsautomat',
    ...(input.product.sku ? { sku: input.product.sku } : {}),
    offers: buildOffers(input.product.priceNet, input.product.rentNet, canonical),
  });

  injectJsonLd(document, 'pdp-breadcrumb-schema', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Shop', item: `${SITE}/${prefix}/shop` },
      { '@type': 'ListItem', position: 2, name: input.product.name, item: canonical },
    ],
  });

  injectJsonLd(document, 'pdp-faq-schema', {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: input.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  });
}

/**
 * Ein oder zwei Offers (Kauf + optional Miete). Bei zwei Offers zeigt Google in
 * der Regel den niedrigsten Preis als „ab …“ an.
 */
function buildOffers(priceNet: number, rentNet: number | undefined, url: string): object {
  const offer = (price: number, name?: string) => ({
    '@type': 'Offer',
    ...(name ? { name } : {}),
    priceCurrency: 'EUR',
    price,
    availability: 'https://schema.org/InStock',
    itemCondition: 'https://schema.org/NewCondition',
    url,
    seller: { '@type': 'Organization', name: 'marktbox' },
  });
  return rentNet ? [offer(priceNet, 'Kauf'), offer(rentNet, 'Miete')] : offer(priceNet);
}

function setCanonical(document: Document, href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel=canonical]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

function injectJsonLd(document: Document, id: string, obj: object): void {
  document.getElementById(id)?.remove();
  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
}
